'use client';
import { useEffect } from 'react';
import { logError } from '@/lib/errorLogger';

export function GlobalErrorHandler() {
  useEffect(() => {
    // ── Uncaught JS errors (TypeError, ReferenceError, etc.) ─────────────────
    const onError = (event) => {
      logError({
        message:  event.message ?? 'Uncaught error',
        stack:    event.error?.stack,
        source:   event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'window.onerror',
        method:   'window.onerror',
        meta: {
          lineno:   event.lineno,
          colno:    event.colno,
          filename: event.filename,
        },
      });
    };

    // ── Unhandled promise rejections ─────────────────────────────────────────
    const onUnhandledRejection = (event) => {
      const reason = event.reason;
      logError({
        message:    reason?.message ?? String(reason) ?? 'Unhandled promise rejection',
        stack:      reason?.stack,
        source:     'unhandledrejection',
        method:     'window.onunhandledrejection',
        statusCode: reason?.response?.status ?? reason?.status,
        meta: {
          endpoint:  reason?.config?.url ?? reason?.response?.url,
          httpMethod: reason?.config?.method?.toUpperCase(),
        },
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // ── Session-expiry watcher ──────────────────────────────────────────────
  // BFF route handlers set X-Session-Expired only when a logged-in user's
  // refresh token has actually died (never for a plain guest 401 — see
  // src/lib/serverAuth.js) — so this only ever fires on a real session end.
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.headers.get('X-Session-Expired') && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?sessionExpired=1';
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return null;
}

export default GlobalErrorHandler;
